import { useMemo } from 'react';
import * as R from 'remeda';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { IntlShape, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { Link as RouterLink, useHistory, useParams } from 'react-router-dom';
import { z } from 'zod';
import { EngineField } from '@rossum/api-client/engineFields';
import { Engine } from '@rossum/api-client/engines';
import { HttpError } from '@rossum/api-client/errors';
import { PageLayout } from '@rossum/components/page-layout/PageLayout';
import { Tile } from '@rossum/components/tile';
import { Button, Stack } from '@rossum/ui/material';
import { restrictedEngineEditEnabledSelector } from '../../../redux/modules/organizationGroup/selectors';
import { EngineErrorAlert } from '../components/EngineErrorAlert';
import { EngineForm } from '../components/EngineForm';
import { FormSkeleton } from '../components/skeletons/FormSkeleton';
import { TilesListSkeleton } from '../components/skeletons/TilesListSkeleton';
import { TilesList } from '../components/TilesList';
import { TilesListEmptyState } from '../components/TilesListEmptyState';
import { ENGINE_FORM_ID } from '../constants';
import { EngineAddHeader } from '../headers/EngineAddHeader';
import { EngineBasicSettingsHeader } from '../headers/EngineBasicSettingsHeader';
import { getAddNewFieldPath } from '../helpers/addNewFieldPathHelpers';
import { useCreateEngine } from '../hooks/useCreateEngine';
import { useEngine } from '../hooks/useEngine';
import { usePatchEngine } from '../hooks/usePatchEngine';
import { useUnpaginatedEngineFields } from '../hooks/useUnpaginatedEngineFields';
import {
  ENGINE_ID_KEY,
  engineBasicSettingsPath,
  engineFieldDetailPath,
} from '../routes';

const resolveErrorPayload = (error: unknown) => {
  if (error instanceof HttpError) {
    const errorSchema = z
      .object({
        name: z.array(z.string()),
        description: z.array(z.string()),
      })
      .partial();

    return errorSchema.catch({}).parse(error.data);
  }

  return {};
};

export const engineFormSchema = (intl: IntlShape) =>
  z.object({
    engine: z.object({
      name: z.string().min(
        1,
        intl.formatMessage({
          id: 'features.engines.error.form.fieldRequired',
        })
      ),
      description: z.string(),
    }),
  });

const useEngineForm = ({
  error,
  engine,
}: {
  error: unknown;
  engine?: Engine;
}) => {
  const intl = useIntl();
  return useForm<z.TypeOf<ReturnType<typeof engineFormSchema>>>({
    errors: {
      // TODO: Translate some common errors / add some F/E validation.
      engine: R.mapValues(resolveErrorPayload(error), apiError =>
        apiError ? { type: 'api_error', message: apiError[0] } : undefined
      ),
    },
    defaultValues: {
      engine: engine ?? {
        name: '',
        description: '',
      },
    },
    resolver: zodResolver(engineFormSchema(intl)),
  });
};

const AddFieldButton = ({
  engineId,
  type,
}: {
  engineId: number;
  type: 'tabular' | 'header';
}) => {
  const intl = useIntl();

  return (
    <Button
      component={RouterLink}
      to={getAddNewFieldPath(engineId, type)}
      variant="contained"
      color="primary"
      data-cy={`add-${type}-engine-field-button`}
      sx={{
        '&:hover': { color: theme => theme.palette.primary.contrastText },
      }}
    >
      {intl.formatMessage({
        id: 'features.engines.engineDetail.addField',
      })}
    </Button>
  );
};

const sortEngineFieldsByName = (engineFields: EngineField[]) =>
  R.sortBy(engineFields, R.prop('name'));

const dedupeEngineFieldIds = (fields: EngineField[]) =>
  new Set(fields.map(field => field.id));

export const EngineAddPage = () => {
  const history = useHistory();
  const { mutate, error, isLoading: engineIsCreating } = useCreateEngine();

  const { control, handleSubmit } = useEngineForm({ error });

  return (
    <PageLayout
      renderHeader={params => (
        <EngineAddHeader {...params} isSaving={engineIsCreating} />
      )}
    >
      <Stack
        px={4}
        py={8}
        spacing={8}
        id={ENGINE_FORM_ID}
        component="form"
        onSubmit={handleSubmit(({ engine }) => {
          mutate(
            { ...engine, type: 'extractor' },
            {
              onSuccess: engineData =>
                history.push(engineBasicSettingsPath(engineData.id)),
            }
          );
        })}
      >
        <EngineForm control={control} />
      </Stack>
    </PageLayout>
  );
};

type EngineBasicSettingsContentProps = {
  engine: Engine;
};

const EngineBasicSettingsContent = ({
  engine,
}: EngineBasicSettingsContentProps) => {
  const intl = useIntl();

  const {
    mutate,
    error,
    isLoading: engineIsPatching,
  } = usePatchEngine(engine.id);

  const isRestrictedEngineEditEnabled = useSelector(
    restrictedEngineEditEnabledSelector
  );

  const { data: engineFieldsData, status: fieldsStatus } =
    useUnpaginatedEngineFields(
      {
        engine: engine.id,
      },
      { select: sortEngineFieldsByName }
    );

  const { data: unusedEngineFields } = useUnpaginatedEngineFields(
    {
      engine: engine.id,
      used: false,
    },
    {
      select: dedupeEngineFieldIds,
    }
  );

  const [tabularFields, headerFields] = useMemo(() => {
    if (!engineFieldsData) return [[], []];

    return R.partition(engineFieldsData, field => field.tabular);
  }, [engineFieldsData]);

  const { control, handleSubmit } = useEngineForm({
    error,
    engine,
  });

  return (
    <PageLayout
      renderHeader={params => (
        <EngineBasicSettingsHeader
          {...params}
          engine={engine}
          isSaving={engineIsPatching}
        />
      )}
    >
      <Stack
        px={4}
        py={4}
        spacing={4}
        id={ENGINE_FORM_ID}
        component="form"
        onSubmit={handleSubmit(formValues => {
          mutate(formValues.engine);
        })}
      >
        <EngineForm
          control={control}
          disabled={isRestrictedEngineEditEnabled}
        />

        {(
          [
            { key: 'headerFields', data: headerFields, dataCy: 'header' },
            { key: 'tabularFields', data: tabularFields, dataCy: 'tabular' },
          ] as const
        ).map(({ key, data, dataCy }) => {
          return (
            <TilesList
              key={key}
              title={intl.formatMessage({
                id: `features.engines.engineDetail.engineFields.${key}.title`,
              })}
              items={data}
              renderTile={field => (
                <Tile
                  key={field.id}
                  title={field.label}
                  description={field.name}
                  endChips={
                    unusedEngineFields?.has(field.id)
                      ? [
                          {
                            key: 'unused',
                            label: intl.formatMessage({
                              id: 'features.engines.engineDetail.engineFields.unusedWarning',
                            }),
                            color: 'warning' as const,
                          },
                        ]
                      : undefined
                  }
                  to={engineFieldDetailPath(engine.id, field.id)}
                />
              )}
              status={fieldsStatus}
              emptyState={
                <TilesListEmptyState
                  title={intl.formatMessage({
                    id: 'features.engines.engineDetail.engineFields.empty.title',
                  })}
                  subtitle={intl.formatMessage({
                    id: 'features.engines.engineDetail.engineFields.empty.subtitle',
                  })}
                >
                  {isRestrictedEngineEditEnabled ? null : (
                    <AddFieldButton engineId={engine.id} type={dataCy} />
                  )}
                </TilesListEmptyState>
              }
              buttons={[
                isRestrictedEngineEditEnabled ? null : (
                  <AddFieldButton
                    engineId={engine.id}
                    key="add-engine-field"
                    type={dataCy}
                  />
                ),
              ]}
            />
          );
        })}
      </Stack>
    </PageLayout>
  );
};

export const EngineBasicSettingsPage = () => {
  const params = useParams<{ [ENGINE_ID_KEY]: string }>();
  const engineIdNumber = parseInt(params[ENGINE_ID_KEY], 10);
  const {
    data: engine,
    isLoading: engineIsLoading,
    isError,
  } = useEngine(engineIdNumber);

  if (isError) {
    return (
      <PageLayout>
        <EngineErrorAlert />
      </PageLayout>
    );
  }

  if (engineIsLoading) {
    return (
      <PageLayout>
        <FormSkeleton />
        <TilesListSkeleton />
      </PageLayout>
    );
  }

  if (!engine) {
    return null;
  }

  return <EngineBasicSettingsContent engine={engine} />;
};
